import moment from 'moment';

const formatMinMaxValues = (netSales = []) => {
  const mappedNetSales = netSales?.map((item) => item.netSales);
  const highestDataValue = Math.max(...mappedNetSales);
  const lowestDataValue = Math.min(...mappedNetSales);
  const maxBufferValue = highestDataValue * 0.1;
  const minBufferValue = lowestDataValue * 0.1;
  const maxTick = highestDataValue >= 1 ? highestDataValue + maxBufferValue : 1;
  const minTick = lowestDataValue <= -1 ? lowestDataValue + minBufferValue : undefined;
  // Note: The reason for undefined on line 10, is that if there are no negative values being displayed
  // and 'min' is set manually, even to a null value, the appearance of the Line graph will no longer match the wireframe
  // when positive numbers present grow into larger numbers (1000+).
  // In case of only positive values on the graph (so, no minTick value needs calculated),
  // minTick must return undefined to preserve visual parity.
  // Max tick will return 1 if there are no positive data values provided,
  // otherwise the chart will lose visual parity when all values are 0.

  return { minTick, maxTick };
};

const getGraphOptions = (netSales = []) => {
  const { minTick, maxTick } = formatMinMaxValues(netSales);

  return {
    maintainAspectRatio: false,
    legend: { display: false },
    scales: {
      xAxes: [
        {
          ticks: { display: true },
          gridLines: {
            display: false,
            drawBorder: true,
          },
        },
      ],
      yAxes: [
        {
          ticks: {
            display: false,
            suggestedMin: 0,
            max: maxTick,
            min: minTick,
          },
          gridLines: {
            display: false,
            drawBorder: true,
          },
        },
      ],
    },
  };
};

const dailySalesTooltipOptions = {
  tooltips: {
    callbacks: {
      label(tooltipItem) {
        const value = Math.floor(Math.abs(tooltipItem.yLabel));
        let formattedValue;

        if (value / 1000 >= 1 && value / 1000 < 1000) {
          formattedValue = `${(value / 1000).toFixed(2)}K`;
        } else if (value / 1000 >= 1000) {
          formattedValue = `${(value / 1000000).toFixed(2)}M`;
        } else {
          formattedValue = value;
        }

        return `${tooltipItem.yLabel < 0 ? '-' : ''}$${formattedValue}`;
      },
    },
  },
};

const graphOptionsForManagerSales = ({ filterType, dateFrom, dateTo }) => {
  let xAxesTimeUnit;

  switch (filterType) {
    case 'WEEK_TO_DATE':
      xAxesTimeUnit = 'day';
      break;
    case 'MONTH_TO_DATE':
      xAxesTimeUnit = 'week';
      break;
    case 'QUARTER_TO_DATE':
      xAxesTimeUnit = 'month';
      break;
    case 'YEAR_TO_DATE':
      xAxesTimeUnit = 'quarter';
      break;

    default:
      xAxesTimeUnit = 'day';
  }

  if (filterType === 'CUSTOM') {
    const daysDiff = Math.abs(moment(dateFrom).diff(moment(dateTo), 'days'));

    if (daysDiff < 8) {
      xAxesTimeUnit = 'day';
    } else if (daysDiff >= 8 && daysDiff < 32) {
      xAxesTimeUnit = 'week';
    } else if (daysDiff >= 32 && daysDiff < 93) {
      xAxesTimeUnit = 'month';
    } else if (daysDiff >= 93 && daysDiff < 365) {
      xAxesTimeUnit = 'quarter';
    } else if (daysDiff > 365) {
      xAxesTimeUnit = 'year';
    }
  }

  return {
    maintainAspectRatio: false,
    legend: { display: false },
    scales: {
      xAxes: [
        {
          type: 'time',
          time: {
            unit: xAxesTimeUnit,
            tooltipFormat: 'MMM DD, YYYY',
            displayFormats: {
              day: 'MMM DD',
              week: 'MMM DD',
              month: 'MMM',
              quarter: 'MMM',
              year: 'YYYY',
            },
          },
          ticks: {
            display: true,
          },
          gridLines: {
            color: '#0000008A',
            drawOnChartArea: false,
          },
        },
      ],
      yAxes: [
        {
          position: 'right',
          ticks: {
            display: true,
            beginAtZero: false,
            callback(value) {
              const absValue = Math.abs(value);
              let label;

              if (absValue / 1000 >= 1 && absValue / 1000 < 1000) {
                const computedValue = absValue / 1000;
                label = `${computedValue % 1 !== 0 ? computedValue.toFixed(2) : computedValue}K`;
              } else if (absValue / 1000 >= 1000) {
                const computedValue = absValue / 1000000;
                label = `${computedValue % 1 !== 0 ? computedValue.toFixed(2) : computedValue}M`;
              } else {
                label = absValue;
              }

              return `${value < 0 ? '-' : ''}$${label}`;
            },
          },
          gridLines: {
            display: true,
            drawBorder: true,
          },
        },
      ],
    },
    ...dailySalesTooltipOptions,
  };
};

const getGraphData = (canvas, data) => {
  const ctx = canvas.getContext('2d');
  const gradient = ctx.createLinearGradient(0, 0, 0, 130);
  gradient.addColorStop(0, 'rgba(0, 133, 124, .34)');
  gradient.addColorStop(1, 'rgba(0, 133, 124, 0)');

  const mappedLabels = [];
  const mappedNetSales = [];

  data.forEach((item) => {
    const date = moment(item?.date).format('dd');

    mappedLabels.push(date);
    mappedNetSales.push(item?.netSales);
  });

  return {
    labels: mappedLabels,
    datasets: [
      {
        fill: true,
        label: 'Daily Net Sales',
        lineTension: 0,
        backgroundColor: gradient,
        borderColor: '#00857C',
        borderCapStyle: 'round',
        borderDash: [],
        borderDashOffset: 0.0,
        borderJoinStyle: 'miter',
        pointBorderColor: '#00857C',
        pointBackgroundColor: '#00857C',
        pointBorderWidth: 1,
        pointHoverRadius: 5,
        pointHoverBackgroundColor: 'rgba(75,192,192,1)',
        pointHoverBorderColor: 'rgba(220,220,220,1)',
        pointHoverBorderWidth: 2,
        pointRadius: 1,
        pointHitRadius: 10,
        data: mappedNetSales,
      },
    ],
  };
};

/**
 *
 * @param canvas - <canvas> element ref, specially for Chart.js
 * @param {String} filterType - enum of WEEK_TO_DATE, MONTH_TO_DATE, QUARTER_TO_DATE, YEAR_TO_DATE, CUSTOM
 * @param {Array<{month, date, value}>} inputData - the data structure shaped according to https://wiki.mynmg.com/display/OSP/Associate+Performance+Service#AssociatePerformanceService-6.APIInterface%22GETMANAGERSALESDETAILS%22
 */
const getGraphDataForManagerSales = ({ canvas, filterType, inputData = [] }) => {
  const ctx = canvas.getContext('2d');
  const gradient = ctx.createLinearGradient(0, 0, 0, 260);

  gradient.addColorStop(0, 'rgba(0, 133, 124, .34)');
  gradient.addColorStop(1, 'rgba(0, 133, 124, 0)');

  let preparedData = inputData;

  // input data is provided per day data
  // for `YEAR_TO_DATE` filter type we need to aggregate to 'per month' format
  if (filterType === 'YEAR_TO_DATE') {
    preparedData = inputData.reduce((res, { date, month, value }) => {
      const resItem = res.find((item) => item.month === month);

      if (resItem) {
        resItem.value += Math.round(value);
      } else {
        res.push({ date, month, value });
      }

      return res;
    }, []);
  }

  // Map data structure to satisfy Input Data format of time based axis
  // https://www.chartjs.org/docs/latest/axes/cartesian/time.html
  preparedData = preparedData.reduce(
    (res, { date, value }) => res.concat({ t: moment(date), y: Math.round(value) }),
    [],
  );

  return {
    datasets: [
      {
        data: preparedData,
        backgroundColor: gradient,
        fill: true,
        lineTension: 0,
        borderColor: '#00857C',
        borderCapStyle: 'round',
        borderDash: [],
        borderDashOffset: 0.0,
        borderJoinStyle: 'miter',
        pointBorderColor: '#00857C',
        pointBackgroundColor: '#00857C',
        pointBorderWidth: 1,
        pointHoverRadius: 5,
        pointHoverBackgroundColor: 'rgba(75,192,192,1)',
        pointHoverBorderColor: 'rgba(220,220,220,1)',
        pointHoverBorderWidth: 2,
        pointRadius: 1,
        pointHitRadius: 10,
      },
    ],
  };
};

export {
  formatMinMaxValues,
  graphOptionsForManagerSales,
  getGraphDataForManagerSales,
  getGraphData,
  getGraphOptions,
  dailySalesTooltipOptions,
};
